import type { MetricResult } from '../../types';
import { attachMockRegistryToMetrics } from '../mockRegistryMeta';

/** Richer L2 payloads for metrics that expose footerLink on L1. No footerLink (avoids nested L2). */
const rawMetricResultsL2Mock: Record<string, MetricResult> = {
  revived_sales_by_vehicle_model: {
    display_name: 'Top models from revived sales',
    overline: 'What they bought',
    meta: {
      query:
        'SELECT /* mock L2 */ * FROM revived_sales_by_vehicle_model WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 36,
    description: '16 unique models sold — full breakdown',
    breakdown: [
      { label: 'Super Duty', value: 9, pct: 25 },
      { label: 'F-150', value: 7, pct: 19 },
      { label: 'Bronco', value: 5, pct: 14 },
      { label: 'Explorer', value: 3, pct: 8 },
      { label: 'Escape', value: 2, pct: 6 },
      { label: 'Mustang', value: 2, pct: 6 },
      { label: 'Ranger', value: 2, pct: 6 },
      { label: 'Maverick', value: 1, pct: 3 },
      { label: '8 other models', value: 5, pct: 13, color: 'grey' },
    ],
  },
  revival_prior_crm_status_share: {
    display_name: 'CRM status when we enrolled them',
    overline: 'Where they came from',
    meta: {
      query:
        'SELECT /* mock L2 */ * FROM revival_prior_crm_status_share WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 33,
    format: 'percent',
    description: 'Full CRM status mix at enrollment',
    breakdown: [
      { label: 'Cold / unqualified', value: 67, pct: 67, color: 'info' },
      { label: 'Lost / inactive', value: 22, pct: 22, color: 'error' },
      { label: 'Bad / unworkable', value: 11, pct: 11, color: 'warning' },
      { label: 'Working / in progress', value: 0, pct: 0, color: 'grey' },
      { label: 'Sold / delivered', value: 0, pct: 0, color: 'grey' },
    ],
  },
  revived_sales_by_content_theme: {
    display_name: 'Top converting messages',
    overline: 'What convinced them',
    meta: {
      query:
        'SELECT /* mock L2 */ * FROM revived_sales_by_content_theme WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 8,
    description: 'All converting message themes this period',
    breakdown: [
      { label: 'Priced-to-move inventory', value: 11 },
      { label: 'New vehicle savings vs. MSRP', value: 9 },
      { label: 'Lease specials', value: 6 },
      { label: 'Trade-in value updates', value: 5 },
      { label: 'Model comparison guides', value: 4 },
      { label: 'Service & maintenance reminders', value: 3 },
      { label: 'Finance pre-approval offers', value: 2 },
      { label: 'Event / private sale invites', value: 1 },
    ],
  },
  salesperson_email_capture_rate: {
    display_name: 'Where the front-line gaps are',
    meta: {
      query:
        'SELECT /* mock L2 */ * FROM salesperson_email_capture_rate WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 8,
    table: {
      columns: [
        { key: 'salesperson', label: 'SALESPERSON', type: 'text' },
        { key: 'emailCapture', label: 'EMAIL CAPTURE', type: 'trend' },
        { key: 'voiCapture', label: 'VOI CAPTURE', type: 'trend' },
        { key: 'percentMarkedDead', label: '% MARKED DEAD', type: 'text' },
        { key: 'flagLabel', label: 'FLAG', type: 'badge' },
      ],
      rows: [
        {
          salesperson: 'Marco D.',
          emailCapture: '94%',
          emailCaptureTrend: 'up',
          voiCapture: '88%',
          voiCaptureTrend: 'up',
          percentMarkedDead: '8%',
          flagLabel: 'Strong',
          flagTone: 'success',
        },
        {
          salesperson: 'Priya M.',
          emailCapture: '83%',
          voiCapture: '79%',
          voiCaptureTrend: 'up',
          percentMarkedDead: '11%',
          flagLabel: 'Strong',
          flagTone: 'success',
        },
        {
          salesperson: 'Jenna R.',
          emailCapture: '71%',
          voiCapture: '62%',
          voiCaptureTrend: 'down',
          percentMarkedDead: '14%',
          flagLabel: 'Mid-pack',
          flagTone: 'info',
        },
        {
          salesperson: 'Chris T.',
          emailCapture: '68%',
          voiCapture: '65%',
          voiCaptureTrend: 'down',
          percentMarkedDead: '18%',
          flagLabel: 'Mid-pack',
          flagTone: 'info',
        },
        {
          salesperson: 'Austin',
          emailCapture: '100%',
          voiCapture: '62%',
          voiCaptureTrend: 'down',
          percentMarkedDead: '38%',
          highlightMarkedDead: true,
          flagLabel: 'Coach',
          flagTone: 'warning',
        },
        {
          salesperson: 'Kenysha',
          emailCapture: '100%',
          voiCapture: '65%',
          voiCaptureTrend: 'down',
          percentMarkedDead: '42%',
          highlightMarkedDead: true,
          flagLabel: 'Coach',
          flagTone: 'warning',
        },
        {
          salesperson: 'Stephanie',
          emailCapture: '100%',
          voiCapture: '69%',
          voiCaptureTrend: 'down',
          percentMarkedDead: '46%',
          highlightMarkedDead: true,
          flagLabel: 'Coach',
          flagTone: 'warning',
        },
        {
          salesperson: 'Devon K.',
          emailCapture: '42%',
          voiCapture: '6%',
          voiCaptureTrend: 'down',
          percentMarkedDead: '38%',
          highlightMarkedDead: true,
          flagLabel: 'Coach',
          flagTone: 'warning',
        },
      ],
    },
  },
  content_theme_sent_count: {
    display_name: 'What moved customers during this Time Period',
    meta: {
      query:
        'SELECT /* mock L2 */ * FROM content_theme_sent_count WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 12,
    table: {
      columns: [
        { key: 'label', label: 'CONTENT THEME', type: 'text' },
        { key: 'sent', label: 'SENT', type: 'number' },
        { key: 'engaged', label: 'ENGAGED', type: 'number' },
        { key: 'converted', label: 'CONVERTED', type: 'bar' },
      ],
      rows: [
        {
          label: '2026 Honda CR-V $196/Mo Special',
          sent: 6415,
          engaged: 446,
          converted: 27,
          share: 100,
        },
        {
          label: '2026 Honda Accord Hybrid Lease Special',
          sent: 4884,
          engaged: 415,
          converted: 24,
          share: 89,
        },
        {
          label: 'Pump Beater Deal: 2026 CR-V Hybrid',
          sent: 7037,
          engaged: 537,
          converted: 22,
          share: 81,
        },
        {
          label: 'Summer Sale - 2026 Honda HR-V Sport AWD',
          sent: 4343,
          engaged: 342,
          converted: 20,
          share: 74,
        },
        {
          label: 'Lease-Ready at $238 - 2026 Honda CRV',
          sent: 9070,
          engaged: 757,
          converted: 17,
          share: 63,
        },
        {
          label: '$189/Month for 2026 Accord',
          sent: 6701,
          engaged: 496,
          converted: 16,
          share: 59,
        },
        {
          label: 'Honda Civic Lease Special',
          sent: 3181,
          engaged: 286,
          converted: 16,
          share: 59,
        },
        {
          label: 'Fuel Smarter - 2026 CR-V Hybrid',
          sent: 2637,
          engaged: 132,
          converted: 11,
          share: 41,
        },
        {
          label: '$177/Month Accord Lease',
          sent: 3952,
          engaged: 300,
          converted: 14,
          share: 52,
        },
        {
          label: 'IN STOCK - CR-V Sport Hybrid',
          sent: 5733,
          engaged: 419,
          converted: 22,
          share: 81,
        },
        {
          label: 'Sport Touring Accord Hybrid',
          sent: 4954,
          engaged: 386,
          converted: 15,
          share: 56,
        },
        {
          label: 'Honda Prelude lease',
          sent: 5781,
          engaged: 448,
          converted: 18,
          share: 67,
        },
      ],
    },
  },
  buyer_segment_count: {
    display_name: 'How each audience is performing',
    meta: {
      query:
        'SELECT /* mock L2 */ * FROM buyer_segment_count WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 10,
    description: 'Full buyer segment breakdown',
    table: {
      columns: [
        { key: 'label', label: 'SEGMENT', type: 'text' },
        { key: 'inNurturing', label: 'IN NURTURING', type: 'number' },
        { key: 'newThisMonth', label: 'NEW THIS MONTH', type: 'number' },
        { key: 'revived', label: 'REVIVED', type: 'number' },
        { key: 'sold', label: 'SOLD', type: 'number' },
        { key: 'conversion', label: 'CONVERSION', type: 'bar' },
      ],
      rows: [
        {
          label: 'Super Duty',
          inNurturing: 2890,
          newThisMonth: 87,
          newChangeLabel: '+87',
          revived: 18,
          sold: 9,
          conversion: '67%',
          share: 67,
        },
        {
          label: 'F-150',
          inNurturing: 4412,
          newThisMonth: 124,
          newChangeLabel: '+124',
          revived: 24,
          sold: 12,
          conversion: '100%',
          share: 100,
        },
        {
          label: 'Bronco',
          inNurturing: 1876,
          newThisMonth: 45,
          newChangeLabel: '+45',
          revived: 11,
          sold: 5,
          conversion: '100%',
          share: 100,
        },
        {
          label: 'Explorer',
          inNurturing: 2104,
          newThisMonth: 64,
          newChangeLabel: '+64',
          revived: 8,
          sold: 4,
          conversion: '100%',
          share: 100,
        },
        {
          label: 'Mustang',
          inNurturing: 982,
          newThisMonth: 28,
          newChangeLabel: '+28',
          revived: 4,
          sold: 2,
          conversion: '100%',
          share: 100,
        },
        {
          label: 'Expedition',
          inNurturing: 756,
          newThisMonth: 19,
          newChangeLabel: '+19',
          revived: 3,
          sold: 0,
          conversion: '—',
        },
        {
          label: 'Escape',
          inNurturing: 1402,
          newThisMonth: 38,
          newChangeLabel: '+38',
          revived: 6,
          sold: 3,
          conversion: '75%',
          share: 75,
        },
        {
          label: 'Maverick',
          inNurturing: 1188,
          newThisMonth: 52,
          newChangeLabel: '+52',
          revived: 7,
          sold: 4,
          conversion: '80%',
          share: 80,
        },
        {
          label: 'Bronco Sport',
          inNurturing: 934,
          newThisMonth: 31,
          newChangeLabel: '+31',
          revived: 5,
          sold: 2,
          conversion: '67%',
          share: 67,
        },
        {
          label: 'Other models',
          inNurturing: 612,
          newThisMonth: 22,
          newChangeLabel: '+22',
          revived: 2,
          sold: 1,
          conversion: '50%',
          share: 50,
          barColor: 'grey',
        },
      ],
    },
  },
};

export const metricResultsL2Mock = attachMockRegistryToMetrics(rawMetricResultsL2Mock);
